import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "@app/lib/utils";

export const buttonVariants = cva(
  "inline-flex min-h-10 max-w-full items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold leading-5 transition-all duration-150 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-5 [&_svg]:w-5 [&_svg]:shrink-0 [&_svg]:stroke-[2]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-soft hover:bg-primary-600 active:bg-primary-700",
        destructive:
          "border border-destructive/25 bg-destructive/10 text-destructive shadow-none hover:border-destructive/35 hover:bg-destructive/15 active:bg-destructive/20",
        success:
          "border border-success/25 bg-success/10 text-success shadow-none hover:border-success/35 hover:bg-success/15 active:bg-success/20",
        warning:
          "border border-warning bg-warning text-warning-foreground shadow-soft hover:border-warning/90 hover:bg-warning/90 active:border-warning/80 active:bg-warning/80",
        info: "border border-info/25 bg-info/10 text-info shadow-none hover:border-info/35 hover:bg-info/15 active:bg-info/20",
        outline:
          "border border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted active:bg-muted",
        outlinePrimary:
          "border border-primary/30 bg-card text-primary shadow-none hover:border-primary/45 hover:bg-primary/10 active:bg-primary/15",
        outlineSuccess:
          "border border-success/30 bg-card text-success shadow-none hover:border-success/45 hover:bg-success/10 active:bg-success/15",
        outlineDestructive:
          "border border-destructive/30 bg-card text-destructive shadow-none hover:border-destructive/45 hover:bg-destructive/10 active:bg-destructive/15",
        outlineWarning:
          "border border-warning/30 bg-card text-warning shadow-none hover:border-warning/45 hover:bg-warning/10 active:bg-warning/15",
        outlineInfo:
          "border border-info/30 bg-card text-info shadow-none hover:border-info/45 hover:bg-info/10 active:bg-info/15",
        outlineMuted:
          "border border-border bg-card text-muted-foreground shadow-none hover:border-foreground/20 hover:bg-muted active:bg-muted",
        secondary:
          "border border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted active:bg-muted",
        ghost:
          "border border-transparent bg-transparent text-foreground shadow-none hover:bg-muted active:bg-muted",
        link: "border border-transparent bg-transparent text-primary shadow-none underline-offset-4 hover:bg-transparent hover:text-primary-600 hover:underline active:text-primary-700",
      },
      size: {
        default: "h-11 rounded-md px-5 py-2 text-sm",
        sm: "h-10 rounded-md px-4 py-2 text-sm",
        lg: "h-12 rounded-md px-6 py-2.5 text-base",
        icon: "h-10 w-10 rounded-md",
        none: "h-auto p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonPrimitiveProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const ButtonPrimitive = React.forwardRef<
  HTMLButtonElement,
  ButtonPrimitiveProps
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});

ButtonPrimitive.displayName = "ButtonPrimitive";
